import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';

import { getUsd1PointApi, getUsd1PointHistoryApi } from '@flipster/apis/v1/user';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { authBunja } from '../shared/auth/state';

export const usd1PointsBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const getUsd1PointApiQueryAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getUsd1PointApi', isCookieLoggedIn],
      queryFn: getUsd1PointApi,
      select: (response: Awaited<ReturnType<typeof getUsd1PointApi>>) => response.data,
      refetchOnMount: 'always',
      enabled: !!isCookieLoggedIn,
    };
  });

  const usd1PointsAtom = atom((get) => get(getUsd1PointApiQueryAtom).data);
  const isLoadingAtom = atom((get) => get(getUsd1PointApiQueryAtom).isLoading);
  const isErrorAtom = atom((get) => get(getUsd1PointApiQueryAtom).isError);
  return {
    usd1PointsAtom,
    isLoadingAtom,
    isErrorAtom,
  };
});

export const usd1PointsHistoryBunja = bunja(() => {
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const store = bunja.use(JotaiStoreScope);

  const getUsd1PointHistoryInfiniteQueryAtom = atomWithInfiniteQuery(() => {
    const isCookieLoggedIn = store.get(isCookieLoggedInAtom);
    return {
      queryKey: ['getUsd1PointHistoryApi', isCookieLoggedIn],
      queryFn: ({ pageParam }: { pageParam?: { cursor: string } }) => getUsd1PointHistoryApi(pageParam),
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (!lastPage.data.hasNext || lastPage.data.pointHistories.length === 0) {
          return undefined;
        }
        const lastItem = lastPage.data.pointHistories[lastPage.data.pointHistories.length - 1];
        return { cursor: `${lastItem.id.toString()}##${lastItem.createdAt}` };
      },
      enabled: !!isCookieLoggedIn,
      refetchOnMount: 'always',
    };
  });

  const usd1PointsHistoryAtom = atom((get) => {
    const infiniteQuery = get(getUsd1PointHistoryInfiniteQueryAtom);
    return infiniteQuery.data?.pages.flatMap((page) => page.data.pointHistories) || [];
  });

  const isLoadingAtom = atom((get) => get(getUsd1PointHistoryInfiniteQueryAtom).isLoading);
  const isFetchingNextPageAtom = atom((get) => get(getUsd1PointHistoryInfiniteQueryAtom).isFetchingNextPage);
  const hasNextPageAtom = atom((get) => get(getUsd1PointHistoryInfiniteQueryAtom).hasNextPage);
  const fetchNextPageAtom = atom(null, (get) => {
    const infiniteQuery = get(getUsd1PointHistoryInfiniteQueryAtom);
    return infiniteQuery.fetchNextPage();
  });

  return {
    usd1PointsHistoryAtom,
    isLoadingAtom,
    isFetchingNextPageAtom,
    hasNextPageAtom,
    fetchNextPageAtom,
  };
});
